import { FormikErrors, useFormik } from 'formik';
import Link from 'next/link';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';

import { closeModal } from '@/store/reducers/modal/slice';
import { setTechnicalError } from '@/store/reducers/settings/slice';

import { createLeadByPartners } from '@/shared/api';
import { PRIVACY_POLICY_PATH } from '@/shared/constants/settings';
import { useTranslate } from '@/shared/hooks/use-translate';
import { Button } from '@/shared/ui/button';
import { ShoppingCardIcon } from '@/shared/ui/icons';
import { CustomInput } from '@/shared/ui/input';

import * as Styled from '../styled';

type TProps = { successNotification: () => void; addLandingProductToBasket: () => void; productId: number };

export const ReservationModal: FC<TProps> = ({ successNotification, addLandingProductToBasket, productId }) => {
    const { t } = useTranslate();

    const dispatch = useDispatch();

    const handleMakeReservation = async ({ phone }: { phone: string }) => {
        try {
            await createLeadByPartners({
                phone: phone.replace(/\D/g, ''),
                referer: window.location.href,
                rentProductId: productId,
            });

            successNotification();

            await dispatch(closeModal());
        } catch (error) {
            /* eslint-disable no-console */
            console.log('createLead error', error);

            dispatch(setTechnicalError({ error }));
        }
    };

    const { handleSubmit, getFieldProps, errors, touched, isValid } = useFormik({
        initialValues: { phone: '' },
        validate: async (values) => {
            const error: FormikErrors<{ iin: string; phone: string }> = {};

            if (values.phone.length < 2) {
                error.phone = 'Обязательное поле';
            } else if (values.phone.replace(/[^\d+]/g, '').length < 12) {
                error.phone = 'Неправильный телефон';
            }

            return error;
        },
        onSubmit: async (formikValues) => {
            await handleMakeReservation(formikValues);
        },
    });

    return (
        <>
            <Styled.ReservationWrapper onSubmit={handleSubmit}>
                <div className="icon-box">
                    <ShoppingCardIcon width="86px" height="86px" />
                </div>
                <div className="inputs">
                    <div className="input-box">
                        <CustomInput
                            label="Ваш номер телефона"
                            {...getFieldProps('phone')}
                            mask="+7 (000) 000-00-00"
                            type="tel"
                        />
                        {errors.phone && touched.phone && (
                            <>
                                <span className="after-input-text error">{t(errors?.phone)}</span>
                                <br />
                            </>
                        )}
                        <span className="after-input-text">
                            {t('Наш оператор свяжется с вами для подтверждения аренды')}
                            {' 🤗'}
                        </span>
                    </div>
                </div>

                <Button
                    aria-label={t('Забронировать через оператора')}
                    variant="primary"
                    type="submit"
                    disabled={!isValid}
                >
                    {t('Забронировать через оператора')}
                </Button>
                <div className="agreement">
                    <span>{t('При нажатии кнопки вы соглашаетесь на')}</span>{' '}
                    <Link className="link-span" href={PRIVACY_POLICY_PATH} target="_blank" rel="noopener noreferrer">
                        {t('сбор и обработку персональных данных')}
                    </Link>
                </div>
                <button
                    aria-label={t('Я хочу забронировать самостоятельно')}
                    className="textButton"
                    type="button"
                    onClick={addLandingProductToBasket}
                >
                    {t('Я хочу забронировать самостоятельно')}
                </button>
            </Styled.ReservationWrapper>
        </>
    );
};
